/**
 * useHarvest Hooks
 *
 * Hooks for harvest recording and yield tracking operations.
 * Connects to the harvest-records Edge Function.
 *
 * User Story: US-P2-007 - Harvest Recording & Yield Tracking
 */

import { useState, useEffect, useCallback } from 'react'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Hook to fetch harvests for a specific container
 * @param {string} containerId - Container UUID
 * @returns {Object} { harvests, summary, loading, error, refetch }
 */
export function useContainerHarvests(containerId) {
  const [harvests, setHarvests] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchHarvests = useCallback(async () => {
    if (!containerId) {
      setHarvests([])
      setSummary(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'get_container_harvests',
            container_id: containerId
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to fetch harvests')
      }

      setHarvests(result.data || [])
      setSummary(result.summary || null)
    } catch (err) {
      console.error('[useContainerHarvests] Error:', err)
      setError(err.message)
      setHarvests([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [containerId])

  useEffect(() => {
    fetchHarvests()
  }, [fetchHarvests])

  return {
    harvests,
    summary,
    loading,
    error,
    refetch: fetchHarvests
  }
}

/**
 * Hook to fetch harvests for a production batch
 * @param {string} batchId - Production batch UUID
 * @returns {Object} { harvests, loading, error, refetch }
 */
export function useBatchHarvests(batchId) {
  const [harvests, setHarvests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchHarvests = useCallback(async () => {
    if (!batchId) {
      setHarvests([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'get_batch_harvests',
            batch_id: batchId
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to fetch batch harvests')
      }

      setHarvests(result.data || [])
    } catch (err) {
      console.error('[useBatchHarvests] Error:', err)
      setError(err.message)
      setHarvests([])
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    fetchHarvests()
  }, [fetchHarvests])

  return {
    harvests,
    loading,
    error,
    refetch: fetchHarvests
  }
}

/**
 * Hook to fetch yield metrics for a production batch
 * @param {string} batchId - Production batch UUID
 * @returns {Object} { metrics, loading, error, refetch }
 */
export function useBatchMetrics(batchId) {
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMetrics = useCallback(async () => {
    if (!batchId) {
      setMetrics(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'get_batch_metrics',
            batch_id: batchId
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to fetch metrics')
      }

      setMetrics(result.data || null)
    } catch (err) {
      console.error('[useBatchMetrics] Error:', err)
      setError(err.message)
      setMetrics(null)
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics
  }
}

/**
 * Hook for harvest mutations (create, update, delete)
 * @returns {Object} { recordHarvest, updateHarvest, deleteHarvest, loading, error }
 */
export function useHarvestMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Record a new harvest
   * @param {Object} params - Harvest parameters
   * @param {string} params.container_id - Container UUID (required)
   * @param {string} params.harvest_date - Date in YYYY-MM-DD format (required)
   * @param {number} params.weight_kg - Weight in kg (required)
   * @param {number} [params.blocks_harvested] - Number of blocks (default: 1)
   * @param {string} [params.quality_grade] - A+, A, B, or reject
   * @param {string} [params.harvested_by] - Name of harvester
   * @param {string} [params.notes] - Additional notes
   * @param {number} [params.flush_number] - Override auto-detection
   * @returns {Promise<Object>} Created harvest record
   */
  const recordHarvest = useCallback(async (params) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'record',
            ...params
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to record harvest')
      }

      return result.data
    } catch (err) {
      console.error('[useHarvestMutations] Record error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing harvest record
   * @param {Object} params - Update parameters
   * @param {string} params.harvest_id - Harvest record UUID (required)
   * @param {number} [params.weight_kg] - Updated weight
   * @param {string} [params.quality_grade] - Updated quality grade
   * @param {string} [params.harvested_by] - Updated harvester
   * @param {string} [params.notes] - Updated notes
   * @returns {Promise<Object>} Updated harvest record
   */
  const updateHarvest = useCallback(async (params) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'update',
            ...params
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to update harvest')
      }

      return result.data
    } catch (err) {
      console.error('[useHarvestMutations] Update error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Record a legacy harvest (creates batch/container automatically)
   * Each entry creates a new container in the weekly legacy batch
   * @param {Object} params - Harvest parameters
   * @param {string} params.strain_id - Strain UUID (required)
   * @param {string} params.harvest_date - Date in YYYY-MM-DD format (required)
   * @param {number} params.weight_kg - Weight in kg (required)
   * @param {string} [params.quality_grade] - A+, A, B, or reject
   * @param {string} [params.harvested_by] - Name of harvester
   * @param {string} [params.notes] - Additional notes
   * @param {boolean} [params.mark_as_spent] - Mark block as spent
   * @returns {Promise<Object>} Created harvest record with batch and container info
   */
  const recordLegacyHarvest = useCallback(async (params) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'record_legacy',
            ...params
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to record legacy harvest')
      }

      return result
    } catch (err) {
      console.error('[useHarvestMutations] Legacy record error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a harvest record
   * @param {string} harvestId - Harvest record UUID
   * @returns {Promise<boolean>} Success status
   */
  const deleteHarvest = useCallback(async (harvestId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/harvest-records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'delete',
            harvest_id: harvestId
          })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to delete harvest')
      }

      return true
    } catch (err) {
      console.error('[useHarvestMutations] Delete error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    recordHarvest,
    recordLegacyHarvest,
    updateHarvest,
    deleteHarvest,
    loading,
    error
  }
}

export default useContainerHarvests
